import { useEffect, useState } from 'react';
import { Save, Settings as SettingsIcon, Calendar } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  useBookingSettings,
  useCalendarConnection,
  useConnectGoogle,
  // useConnectMicrosoft,
  // useDisconnectEmailConnection,
  useUpdateBookingSettings,
} from '../../hooks/useBookingSettings';
import { BookingSettingsFormValues, hasValidationErrors, validateBookingSettingsForm } from '../../utils/bookingValidators';
import Spinner from '../../components/Spinner';

const TIMEZONES = [
  'UTC',

  // United Kingdom
  'Europe/London',

  // Singapore
  'Asia/Singapore',

  // Philippines
  'Asia/Manila',

  // UAE
  'Asia/Dubai',

  // South Africa
  'Africa/Johannesburg',

  // Australia
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Brisbane',

  // New Zealand
  'Pacific/Auckland',

  // India
  'Asia/Kolkata',

  // Malaysia
  'Asia/Kuala_Lumpur',

  // Hong Kong
  'Asia/Hong_Kong',

  // Japan
  'Asia/Tokyo',

  // United States
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',

  // Canada
  'America/Toronto',
  'America/Vancouver',

  // Europe
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich'
];

const parseReminderHours = (reminderHours: string) => {
  const hours = reminderHours.split(',').map((h) => h.trim());
  return { hoursBefore24: hours.includes('24'), hoursBefore12: hours.includes('12'), hoursBefore1: hours.includes('1') };
};

const buildReminderHours = (reminders: { hoursBefore24: boolean; hoursBefore12: boolean; hoursBefore1: boolean }) =>
  [reminders.hoursBefore24 && '24', reminders.hoursBefore12 && '12', reminders.hoursBefore1 && '1'].filter(Boolean).join(',');

export default function BookingSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useBookingSettings();
  const { data: connection } = useCalendarConnection();
  const updateSettings = useUpdateBookingSettings();
  const connectGoogle = useConnectGoogle();
  // const connectMicrosoft = useConnectMicrosoft();
  // const disconnectConnection = useDisconnectEmailConnection();

  const [values, setValues] = useState<BookingSettingsFormValues>({
    timezone: 'UTC',
    minimumNoticeHours: 24,
    advanceBookingDays: 30,
    defaultAppointmentDuration: 30,
    bufferMinutes: 15,
    bookingSlug: '',
    primaryColor: '#172554',
    secondaryColor: '#e2e8f0',
    buttonColor: '#7f1d1d',
    accentColor: '#be123c',
  });
  const [reminders, setReminders] = useState({ hoursBefore24: true, hoursBefore12: false, hoursBefore1: true });
  const [calendarSyncEnabled, setCalendarSyncEnabled] = useState(false);
  const [crmSyncEnabled, setCrmSyncEnabled] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      setValues({
        timezone: settings.timezone,
        minimumNoticeHours: settings.minimumNoticeHours,
        advanceBookingDays: settings.advanceBookingDays,
        defaultAppointmentDuration: settings.defaultAppointmentDuration,
        bufferMinutes: settings.bufferMinutes,
        bookingSlug: settings.bookingSlug ?? '',
        primaryColor: settings.primaryColor || '#172554',
        secondaryColor: settings.secondaryColor || '#e2e8f0',
        buttonColor: settings.buttonColor || '#7f1d1d',
        accentColor: settings.accentColor || '#be123c',
      });

      setReminders(parseReminderHours(settings.reminderHours));
      setCalendarSyncEnabled(settings.calendarSyncEnabled);
      setCrmSyncEnabled(settings.crmSyncEnabled);
    }
  }, [settings]);

  useEffect(() => {
    const success = searchParams.get('success');
    const provider = searchParams.get('provider');
    if (success === 'true' && provider) {
      const providerName = provider === 'google' ? 'Google' : provider === 'microsoft' ? 'Microsoft' : provider;
      toast.success(`Successfully connected ${providerName} Calendar.`);
      setSearchParams({}, { replace: true });
      void queryClient.invalidateQueries({ queryKey: ['booking-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar-connection'] });
    }
  }, [queryClient, searchParams, setSearchParams]);

  useEffect(() => {
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty]);

  const handleChange = (field: keyof BookingSettingsFormValues, value: string | number) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleColorChange = (field: 'primaryColor' | 'secondaryColor' | 'buttonColor' | 'accentColor', value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const copyToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.success('Copied to clipboard');
  };

  const handleCalendarToggle = (checked: boolean) => {
    if (checked && !connection?.connected) {
      toast.error('Connect Google or Microsoft Calendar first.');
      return;
    }

    setCalendarSyncEnabled(checked);
    setIsDirty(true);
  };

  const handleSave = () => {
    const validationErrors = validateBookingSettingsForm(values);
    setErrors(validationErrors);
    if (hasValidationErrors(validationErrors)) return;

    if (calendarSyncEnabled && !connection?.connected) {
      toast.error('Connect Google or Microsoft Calendar first.');
      return;
    }

    updateSettings.mutate(
      {
        timezone: values.timezone,
        minimumNoticeHours: Number(values.minimumNoticeHours),
        advanceBookingDays: Number(values.advanceBookingDays),
        defaultAppointmentDuration: Number(values.defaultAppointmentDuration),
        bufferMinutes: Number(values.bufferMinutes),
        reminderHours: buildReminderHours(reminders),
        calendarSyncEnabled,
        crmSyncEnabled,
        bookingSlug: values.bookingSlug,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        buttonColor: values.buttonColor,
        accentColor: values.accentColor,
      },
      { onSuccess: () => setIsDirty(false) }
    );
  };

  // const providerStyle = connection?.provider === 'Google'
  //   ? 'bg-red-50 text-red-700 border-red-200'
  //   : connection?.provider === 'Microsoft'
  //     ? 'bg-strivre-lightburgundy text-blue-700 border-blue-200'
  //     : 'bg-strivre-softgrey text-slate-700 border-strivre-lightnavy';

  const bookingUrl = `${window.location.origin}/book/${values.bookingSlug}`;
  const iframeCode = `<iframe src="${bookingUrl}"width="100%" height="900" frameborder="0" style="border:none;border-radius:12px;"></iframe>`;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Spinner size="h-8 w-8" />
        <p className="mt-3 text-strivre-mutedtext">Loading booking configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><SettingsIcon size={22} /> Booking Configuration</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">Control scheduling rules, reminders, and connected integrations.</p>
        </div>
        <button onClick={handleSave} disabled={updateSettings.isPending} className="flex items-center gap-2 bg-strivre-navy text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-strivre-navy/90 disabled:opacity-50">
          <Save size={18} /> {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <section className="bg-white border border-strivre-lightnavy rounded-lg p-5 space-y-4 text-left">
        <h3 className="text-base font-semibold text-strivre-navy text-left">General</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Timezone</label>
            <select className="input-base" value={values.timezone} onChange={(e) => handleChange('timezone', e.target.value)}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            {errors.timezone && <p className="mt-1 text-xs text-strivre-burgundy">{errors.timezone}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">
              Booking URL Slug
            </label>

            <input
              className="input-base"
              value={values.bookingSlug}
              onChange={(e) =>
                handleChange(
                  'bookingSlug',
                  e.target.value
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '')
                )
              }
              placeholder="strivre-services"
            />

            {/* <p className="mt-1 text-xs text-slate-500">
              Your public booking page will use this URL.
            </p>

            <p className="mt-1 text-xs font-medium text-strivre-navy">
              {window.location.origin}/book/{values.bookingSlug}
            </p> */}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Minimum Booking Notice (hours)</label>
            <input type="number" className="input-base" value={values.minimumNoticeHours} onChange={(e) => handleChange('minimumNoticeHours', e.target.value)} />
            {errors.minimumNoticeHours && <p className="mt-1 text-xs text-strivre-burgundy">{errors.minimumNoticeHours}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Maximum Booking Window (days)</label>
            <input type="number" className="input-base" value={values.advanceBookingDays} onChange={(e) => handleChange('advanceBookingDays', e.target.value)} />
            {errors.advanceBookingDays && <p className="mt-1 text-xs text-strivre-burgundy">{errors.advanceBookingDays}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Default Appointment Duration (min)</label>
            <input type="number" className="input-base" value={values.defaultAppointmentDuration} onChange={(e) => handleChange('defaultAppointmentDuration', e.target.value)} />
            {errors.defaultAppointmentDuration && <p className="mt-1 text-xs text-strivre-burgundy">{errors.defaultAppointmentDuration}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Buffer Time (min)</label>
            <input type="number" className="input-base" value={values.bufferMinutes} onChange={(e) => handleChange('bufferMinutes', e.target.value)} />
            {errors.bufferMinutes && <p className="mt-1 text-xs text-strivre-burgundy">{errors.bufferMinutes}</p>}
          </div>
        </div>
      </section>

      <section className="bg-white border border-strivre-lightnavy rounded-lg p-5 space-y-3">
        <h3 className="text-sm font-bold text-strivre-navy">Reminder Configuration</h3>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-strivre-darktext">
            <input type="checkbox" checked={reminders.hoursBefore24} onChange={(e) => { setReminders({ ...reminders, hoursBefore24: e.target.checked }); setIsDirty(true); }} />
            24 Hours Before
          </label>
          <label className="flex items-center gap-2 text-sm text-strivre-darktext">
            <input type="checkbox" checked={reminders.hoursBefore12} onChange={(e) => { setReminders({ ...reminders, hoursBefore12: e.target.checked }); setIsDirty(true); }} />
            12 Hours Before
          </label>
          <label className="flex items-center gap-2 text-sm text-strivre-darktext">
            <input type="checkbox" checked={reminders.hoursBefore1} onChange={(e) => { setReminders({ ...reminders, hoursBefore1: e.target.checked }); setIsDirty(true); }} />
            1 Hour Before
          </label>
        </div>
      </section>

      <section className="bg-white border border-strivre-lightnavy rounded-xl p-6">
        <div className="flex items-start justify-between">
            <div>
            <h3 className="text-base font-semibold text-strivre-navy text-left">
                Calendar Integration
            </h3>
            <p className="text-sm text-strivre-mutedtext mt-1">
                Sync bookings with your connected calendar provider.
            </p>
            </div>

            <label className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600">
                Sync Enabled
            </span>

            <input
                type="checkbox"
                checked={calendarSyncEnabled}
                disabled={!connection?.connected}
                onChange={(e) =>
                handleCalendarToggle(e.target.checked)
                }
            />
            </label>
        </div>

        <div className="mt-6">
            {connection?.connected ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-start justify-between">
                <div className="flex gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white border border-green-200">
                    <Calendar size={18} className="text-green-600" />
                    </div>

                    <div>
                    <h4 className="font-semibold text-green-800">
                        {connection.provider} Calendar
                    </h4>

                    <p className="text-sm text-green-700">
                        {connection.email}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                        Connected
                        </span>

                        <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                            calendarSyncEnabled
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-strivre-softgrey text-slate-700'
                        }`}
                        >
                        Sync {calendarSyncEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                    </div>
                </div>

                {/* <button
                    type="button"
                    onClick={() =>
                    disconnectConnection.mutate()
                    }
                    disabled={disconnectConnection.isPending}
                    className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                    {disconnectConnection.isPending
                    ? 'Disconnecting...'
                    : 'Disconnect'}
                </button> */}
                </div>
            </div>
            ) : (
            <div className="space-y-4">
                <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
                <p className="font-medium text-slate-900">
                    No calendar connected
                </p>

                <p className="mt-1 text-sm text-slate-600">
                    Connect Google Calendar or Microsoft Outlook
                    Calendar to automatically create booking
                    events.
                </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Google */}
                  <button
                    type="button"
                    onClick={() => connectGoogle.mutate()}
                    disabled={connectGoogle.isPending}
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {connectGoogle.isPending
                      ? "Connecting..."
                      : "Connect Google"}
                  </button>

                  {/* Microsoft - Coming Soon */}
                  <div className="relative">
                    <span className="absolute -right-2 -top-2 z-10 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 shadow-sm">
                      Coming Soon
                    </span>

                    <button
                      type="button"
                      disabled
                      className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500 opacity-80"
                    >
                      Connect Microsoft
                    </button>
                  </div>
                </div>
            </div>
            )}
        </div>
      </section>
      <section className="bg-white border border-strivre-lightnavy rounded-xl p-6">
        <div className="flex items-start justify-between">
            <div>
            <h3 className="text-base font-semibold text-strivre-navy">
                CRM Integration
            </h3>

            <p className="text-sm text-strivre-mutedtext mt-1">
                Automatically create and update contacts and appointments.
            </p>
            </div>

            <label className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600">
                Sync Enabled
            </span>

            <input
                type="checkbox"
                checked={crmSyncEnabled}
                onChange={(e) => {
                setCrmSyncEnabled(e.target.checked);
                setIsDirty(true);
                }}
            />
            </label>
        </div>

        <div className="mt-6">
            {settings?.crmSyncEnabled ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                <h4 className="font-semibold text-green-800">
                CRM Connected
                </h4>

                <p className="text-sm text-green-700 mt-1">
                CRM synchronization is enabled.
                </p>

                <div className="mt-3">
                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    Sync Active
                </span>
                </div>
            </div>
            ) : (
            <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
                <p className="font-medium text-slate-900">
                No CRM Connected
                </p>

                <p className="mt-1 text-sm text-slate-600">
                Connect HubSpot, Salesforce, Zoho, or Dynamics 365
                to automatically create contacts and appointments.
                </p>
            </div>
            )}
        </div>
      </section>
      <section className="bg-white border border-strivre-lightnavy rounded-xl p-6">
        <h3 className="text-base font-semibold text-strivre-navy">Brand Colors</h3>
        <p className="text-sm text-strivre-mutedtext mt-1">
          Applied to the public booking page, availability screens, confirmation forms, and embedded widgets.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {(['primaryColor', 'secondaryColor', 'buttonColor', 'accentColor'] as const).map((field) => (
            <label key={field} className="flex items-center gap-3 text-xs font-semibold capitalize text-slate-600">
              <input
                type="color"
                value={values[field]}
                onChange={(e) => handleColorChange(field, e.target.value)}
                className="h-10 w-12 cursor-pointer rounded border-0 p-0"
              />
              {field.replace('Color', '')}
            </label>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border border-slate-200">
          <div className="border-b border-slate-100 px-5 py-3 text-sm font-bold text-slate-900">Live Preview</div>

          <div style={{ backgroundColor: values.secondaryColor }} className="p-5">
            <div style={{ backgroundColor: values.primaryColor }} className="rounded-lg p-5 text-white">
              <p className="text-xs uppercase tracking-wide opacity-70">Book an Appointment</p>
              <h3 className="mt-1 text-xl font-bold">{values.bookingSlug || 'Your Booking Page'}</h3>

              <button
                type="button"
                style={{ backgroundColor: values.buttonColor }}
                className="mt-5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
              >
                Continue
              </button>
            </div>

            <div className="mt-3 rounded-lg bg-white p-4">
              <p className="font-semibold text-slate-800">Step 2 of 4</p>
              <p className="mt-1 text-xs" style={{ color: values.accentColor }}>
                Select an available time slot
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-strivre-lightnavy rounded-xl p-6">
        <div>
          <h3 className="text-base font-semibold text-strivre-navy">
            Booking Widget & Sharing
          </h3>

          <p className="text-sm text-strivre-mutedtext mt-1">
            Share your booking page directly or embed it into your website.
          </p>
        </div>

        <div className="mt-6 space-y-6">
          
          {/* Public Booking URL */}
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-2">
              Public Booking Page
            </label>

            <div className="flex gap-2">
              <input
                readOnly
                value={bookingUrl}
                className="input-base flex-1"
              />

              <button
                type="button"
                onClick={() => copyToClipboard(bookingUrl)}
                className="px-4 py-2 bg-strivre-navy text-white rounded-lg text-sm font-medium"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Embed Code */}
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-2">
              Website Embed Code
            </label>

            <div className="flex gap-2">
              <input
                readOnly
                value={iframeCode}
                className="input-base flex-1 font-mono"
              />

              <button
                type="button"
                onClick={() => copyToClipboard(iframeCode)}
                className="px-4 py-2 bg-strivre-burgundy text-white rounded-lg text-sm font-medium"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="rounded-lg bg-slate-50 border border-strivre-lightnavy p-4">
            <h4 className="font-medium text-slate-900">
              How to use
            </h4>

            <ul className="mt-2 text-sm text-slate-600 space-y-1 list-disc pl-5">
              <li>Share the Public Booking Page link directly with customers.</li>
              <li>Paste the Embed Code into WordPress, Wix, Shopify, Webflow, or any website that supports HTML embeds.</li>
              <li>Changes to your booking settings automatically apply everywhere.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

